import React, { useEffect } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Grid } from '@mui/material';
import { DIMENSIONALITY_VALUES } from 'constants/businessTool';

import { unitConvertorMultiplier } from 'components/BusinessToolForm/components/CalculationsObserver/helpers/balanceSheet/consts';
import { MONTH_NAMES, PERIOD_NAMES } from 'components/BusinessToolForm/constants/periods';
import { prefix, REVENUE_PLAN_NAMES } from 'components/BusinessToolForm/Sections/FinancialPlan/RevenuePlan/utils/form';
import { GLOBAL_UNIT_NAME } from 'components/BusinessToolForm/Sections/Global/form';

import { concatWithDot } from 'helpers/string';

import { IExpensesInfoRowProps } from './types';
import { ExpenseTable } from '../../../utils/form';
import { MonthlyExpansesField } from '../MonthlyExpansesField';
import { MONTHLY_EXPANSES_FIELD_TYPE } from '../MonthlyExpansesField/type';

export const ExpensesInfoRow: React.FC<IExpensesInfoRowProps> = ({
  expensesName,
  revenueName,
  growthRateName,
  disabled = false,
  watchTableName = '',
}) => {
  const { t } = useTranslation();
  const { control, getValues, setValue } = useFormContext();

  const revenueTable = concatWithDot(prefix, REVENUE_PLAN_NAMES.PLANNED_PRODUCTS_VOLUME_TOTAL);

  const [watchedTable, revenue] = useWatch({
    name: [watchTableName, revenueTable],
  });

  const unit = getValues(GLOBAL_UNIT_NAME);
  const multiplier = unitConvertorMultiplier[unit][DIMENSIONALITY_VALUES.SINGLE];

  const totalFirstMonthRevenue = String(revenue[PERIOD_NAMES.MONTH_VALUES][MONTH_NAMES.M1] * multiplier);

  useEffect(() => {
    const categoryRevenue = getValues(revenueName);
    const categoryExpense = getValues(expensesName);

    if (!categoryRevenue || categoryExpense) {
      return;
    }

    const firstMonthRevenueNum = Number(totalFirstMonthRevenue);
    if (!firstMonthRevenueNum) {
      return;
    }

    const calculatedRevenue = (categoryRevenue / 100) * firstMonthRevenueNum;

    const expensesCount = isNaN(calculatedRevenue) || !isFinite(calculatedRevenue) ? '' : calculatedRevenue;

    setValue(expensesName, expensesCount);
  }, []);

  return (
    <Grid container spacing={2} alignItems="flex-end">
      <Grid item xs={4}>
        <MonthlyExpansesField
          name={expensesName}
          watchFiledName={revenueName}
          label={t('financialPlan.plannedExpenses.monthlyExpenses.plannedMonthlyExpense')}
          adornmentType="currency"
          control={control}
          disabled={disabled}
          fieldType={MONTHLY_EXPANSES_FIELD_TYPE.AMOUNT}
          totalFirstMonthRevenue={totalFirstMonthRevenue}
        />
      </Grid>
      <Grid item xs={4}>
        <MonthlyExpansesField
          name={revenueName}
          watchFiledName={expensesName}
          label={t('financialPlan.plannedExpenses.monthlyExpenses.shareOfExpenses')}
          adornmentType="percentage"
          control={control}
          disabled={disabled}
          fieldType={MONTHLY_EXPANSES_FIELD_TYPE.PERCENT}
          totalFirstMonthRevenue={totalFirstMonthRevenue}
        />
      </Grid>
      <Grid item xs={4}>
        <MonthlyExpansesField
          control={control}
          name={growthRateName}
          disabled={disabled}
          label={t('financialPlan.plannedExpenses.monthlyExpenses.annualExpenseGrowthRate')}
          adornmentType="percentage"
          watchFiledName={watchTableName}
          fieldType={MONTHLY_EXPANSES_FIELD_TYPE.GROWTH}
          watchedTableValues={watchTableName ? (watchedTable as ExpenseTable[]) : null}
          totalFirstMonthRevenue={totalFirstMonthRevenue}
        />
      </Grid>
    </Grid>
  );
};
